// context/RegionsContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Client, getSessionToken, settings } from "../library/allauth";
import { getCSRFToken } from "../library/django";

const RegionsContext = createContext();

export const useRegions = () => {
  const context = useContext(RegionsContext);
  if (!context) {
    throw new Error("useRegions must be used within a RegionsProvider");
  }
  return context;
};

export const RegionsProvider = ({ children }) => {
  const [regions, setRegions] = useState([]);
  const [allSystems, setAllSystems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [authRetryCount, setAuthRetryCount] = useState(0);

  // Cache for systems by site - prevents re-fetching
  const [systemsCache, setSystemsCache] = useState({});
  const [loadingSystems, setLoadingSystems] = useState({});

  // Helper to get fetch options
  const getFetchOptions = useCallback(() => {
    let baseUrl = "";
    try {
      baseUrl = settings.baseUrl;
    } catch {}

    const sessionToken = getSessionToken();
    const options = { headers: {} };

    if (settings.client === Client.BROWSER && sessionToken) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        options.headers["X-CSRFToken"] = csrfToken;
      }
    }
    if (settings.withCredentials) {
      options.credentials = "include";
    }

    return { baseUrl, options };
  }, []);

  // Load data once when the provider mounts
  useEffect(() => {
    const loadInitialData = async () => {
      if (dataLoaded) return;

      if (authRetryCount === 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      try {
        setIsLoading(true);
        setError(null);

        const { baseUrl, options } = getFetchOptions();

        // Load municipal regions
        const regionRes = await window.fetch(
          baseUrl + "/customer/get_region_data/",
          options,
        );

        if (regionRes.status === 401) {
          throw new Error("Authentication required. Please log in again.");
        }

        if (regionRes.status === 403) {
          throw new Error(
            "Access denied. You do not have permission to view this data.",
          );
        }

        if (!regionRes.ok) {
          throw new Error(
            `Failed to fetch regions: ${regionRes.status} ${regionRes.statusText}`,
          );
        }

        const regionData = await regionRes.json();

        if (!Array.isArray(regionData)) {
          console.error("Invalid region data format:", regionData);
          throw new Error("Invalid data format received from server");
        }

        if (regionData.length === 0) {
          console.warn("No regions available for this user");
          setRegions([
            {
              region_name: "No Regions Available",
              region_id: "none",
            },
          ]);
          setSelectedRegion(null);
          setAllSystems([]);
          setDataLoaded(true);
          return;
        }

        const regionsWithAll = [
          { region_name: "All", region_id: "all" },
          ...regionData,
        ];

        setRegions(regionsWithAll);

        // Try to restore previously selected region from sessionStorage
        const savedRegion = sessionStorage.getItem("selectedRegion");
        let defaultRegion = null;

        if (savedRegion) {
          try {
            const parsed = JSON.parse(savedRegion);
            // Verify the saved region still exists in available regions
            defaultRegion = regionData.find(
              (r) => r.region_id === parsed.region_id,
            );
          } catch (e) {
            console.error("Error parsing saved region:", e);
          }
        }

        // If no saved region or saved region not found, use first available
        if (!defaultRegion && regionData.length > 0) {
          defaultRegion = regionData[0];
        }

        if (defaultRegion) {
          setSelectedRegion(defaultRegion);
        }

        // Load all systems
        const systemsRes = await window.fetch(
          baseUrl + "/customer/get_systems_list/",
          options,
        );

        if (systemsRes.status === 401) {
          throw new Error("Authentication required. Please log in again.");
        }

        if (!systemsRes.ok) {
          throw new Error(
            `Failed to fetch systems: ${systemsRes.status} ${systemsRes.statusText}`,
          );
        }

        const systemsData = await systemsRes.json();

        if (!Array.isArray(systemsData)) {
          console.error("Invalid systems data format:", systemsData);
          throw new Error("Invalid data format received from server");
        }

        setAllSystems(systemsData);
        setDataLoaded(true);
      } catch (err) {
        console.error("Error loading regions data:", err);

        if (
          err.message &&
          err.message.includes("Authentication required") &&
          authRetryCount < 2
        ) {
          setAuthRetryCount((prev) => prev + 1);
          setTimeout(() => {
            setDataLoaded(false);
            loadInitialData();
          }, 1000);
          return;
        }

        setError(err.message || "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [dataLoaded, authRetryCount, getFetchOptions]);

  // Function to fetch sites for a specific region
  const fetchSystemsByRegion = useCallback(
    async (regionId) => {
      try {
        const { baseUrl, options } = getFetchOptions();

        const url =
          regionId && regionId !== "all"
            ? `${baseUrl}/customer/get_systems_list/?region_id=${regionId}`
            : `${baseUrl}/customer/get_systems_list/`;

        const systemsRes = await window.fetch(url, options);

        if (!systemsRes.ok) {
          throw new Error(`Failed to fetch sites`);
        }

        const systemsData = await systemsRes.json();
        setAllSystems(systemsData);
      } catch (err) {
        console.error("Error fetching systems:", err);
      }
    },
    [getFetchOptions],
  );
  // NEW: Refetch sites when selected region changes
  useEffect(() => {
    if (selectedRegion && dataLoaded) {
      fetchSystemsByRegion(selectedRegion.region_id);
    }
  }, [selectedRegion, dataLoaded, fetchSystemsByRegion]);


  // Helper functions
  const getSystemsByRegion = useCallback(
    (regionId) => {
      if (regionId === "all") {
        return allSystems;
      }
      return allSystems.filter((s) => s.system_region_id === regionId);
    },
    [allSystems],
  );

  const getRegionById = useCallback(
    (regionId) => {
      return regions.find((r) => r.region_id === regionId);
    },
    [regions],
  );

  const getSystemById = useCallback(
    (systemId) => {
      return allSystems.find((s) => s.system_id === systemId);
    },
    [allSystems],
  );

  const value = {
    // State
    regions,
    allSystems,
    isLoading,
    error,
    selectedRegion,
    setSelectedRegion: (region) => {
      setSelectedRegion(region);
      if (region) {
        sessionStorage.setItem("selectedRegion", JSON.stringify(region));
      }
    },

    // Helper functions
    getSystemsByRegion,
    getRegionById,
    getSystemById,

    // Reload
    refetchData: () => {
      setDataLoaded(false);
      setAuthRetryCount(0);
      setSystemsCache({});
    },
  };

  return (
    <RegionsContext.Provider value={value}>{children}</RegionsContext.Provider>
  );
};
